import { z } from "zod";

import {
  authPhoneSchema,
  loginPasswordSchema,
  makeAuthNameSchema,
  newPasswordSchema,
  normalizedEmailSchema,
} from "@/lib/validation/auth";
import { userSchema } from "../user";

export const authLoginRequestSchema = z.object({
  email: normalizedEmailSchema,
  password: loginPasswordSchema,
});

export type TAuthLoginRequestPayload = z.infer<typeof authLoginRequestSchema>;

export type TAuthLoginResponsePayload = {
  code: number;
  message: string;
  data: {
    access_token: string;
    refresh_token: {
      token: string;
      expires_at: string;
    };
    requires_2fa: boolean;
    token_type: string;
  };
};

export const authSignupRequestSchema = userSchema
  .pick({ email: true })
  .extend({
    email: normalizedEmailSchema,
    firstName: makeAuthNameSchema("First name"),
    lastName: makeAuthNameSchema("Last name"),
  })
  .extend({
    phone: authPhoneSchema,
    password: newPasswordSchema,
    confirmPassword: z.string().min(1, "Please re-enter your password"),
    acceptTerms: z.boolean().refine((val) => val === true, {
      message: "You must accept the Terms & Conditions and Privacy Policy",
    }),
  })
  .refine(
    (data) => !newPasswordSchema.safeParse(data.password).success || data.password === data.confirmPassword,
    { path: ["confirmPassword"], message: "Passwords do not match" }
  );

export type TAuthSignupRequestPayload = z.infer<typeof authSignupRequestSchema>;

export const forgotPasswordRequestSchema = z.object({
  email: normalizedEmailSchema,
});

export type TForgotPasswordRequestPayload = z.infer<typeof forgotPasswordRequestSchema>;

export const resetPasswordRequestSchema = z
  .object({
    password: newPasswordSchema,
    confirmPassword: z.string(),
    token: z.string().min(1, "Token is required"),
  })
  .refine(
    (data) => !newPasswordSchema.safeParse(data.password).success || data.password === data.confirmPassword,
    { path: ["confirmPassword"], message: "Passwords do not match" }
  );

export type TAuthResetPasswordRequestPayload = z.infer<typeof resetPasswordRequestSchema>;
